import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { filter, map, take, timeout, catchError } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { User, UserRole } from '../models/user.model';
import { redirectToLogin } from './redirect-to-login';

type AdminAccess = 'allow' | 'redirect-staffing' | 'deny';

@Injectable({ providedIn: 'root' })
export class AdminRoleGuard implements CanActivate, CanActivateChild {
  /** The one admin-area page PD/FD staffing reviewers may reach. */
  private static readonly STAFFING_ROUTE = '/admin/staffing';

  constructor(private authService: AuthService, private router: Router) {}

  canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | boolean | UrlTree {
    return this.checkAdminRole(state);
  }

  canActivateChild(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | boolean | UrlTree {
    return this.checkAdminRole(state);
  }

  /**
   * Admins get the whole admin area. pd_staff / fd_staff (SP fee build
   * slice 2) get exactly the Staffing Review lane — any other admin URL
   * bounces them to it rather than to /login, so their login landing and
   * deep links both work even though every other admin page is hidden.
   */
  private accessFor(role: string | null | undefined, url: string): AdminAccess {
    if (role === UserRole.Admin) return 'allow';
    if (role === UserRole.PdStaff || role === UserRole.FdStaff) {
      return url.startsWith(AdminRoleGuard.STAFFING_ROUTE) ? 'allow' : 'redirect-staffing';
    }
    return 'deny';
  }

  private resolveAccess(user: User | null, url: string): AdminAccess {
    const byUser = this.accessFor(user?.role, url);
    if (byUser !== 'deny') return byUser;
    // JWT is the server-authoritative source for role — use it as fallback
    // in case the in-memory user has stale data from a race with /auth/me
    return this.accessFor(this.authService.getRoleFromToken(), url);
  }

  private applyAccess(access: AdminAccess, state: RouterStateSnapshot): boolean | UrlTree {
    if (access === 'allow') return true;
    if (access === 'redirect-staffing') return this.router.createUrlTree([AdminRoleGuard.STAFFING_ROUTE]);
    // Not authorized at all — bounce to /login carrying returnUrl so a deep
    // link (e.g. an admin-notification email's /admin/locations?... link)
    // survives the round trip instead of dropping the admin onto the
    // generic post-login homepage. See redirect-to-login.ts.
    return redirectToLogin(this.router, state);
  }

  private checkAdminRole(state: RouterStateSnapshot): Observable<boolean | UrlTree> | boolean | UrlTree {
    const user = this.authService.getCurrentUser();
    const url = state.url;

    if (user !== null) {
      return this.applyAccess(this.resolveAccess(user, url), state);
    }

    // No JWT — unauthenticated
    if (!localStorage.getItem('jwt')) {
      return redirectToLogin(this.router, state);
    }

    // JWT present but user still loading — check JWT role directly first
    const tokenAccess = this.accessFor(this.authService.getRoleFromToken(), url);
    if (tokenAccess !== 'deny') return this.applyAccess(tokenAccess, state);

    // JWT role not readable yet (or non-admin) — wait for user$ to resolve
    return this.authService.user$.pipe(
      filter((u): u is User => u !== null),
      take(1),
      timeout(10000),
      map(u => this.applyAccess(this.resolveAccess(u, url), state)),
      catchError(() => of(redirectToLogin(this.router, state)))
    );
  }
}
